import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  ListChecks,
  QrCode,
  ScanLine,
  School,
  UserPlus,
  Users,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Avatar, Button, EmptyState, PageHeader, StatusBadge } from "../components/ui";
import { useApp } from "../state/AppContext";
import {
  formatDate,
  formatDayDate,
  formatTime,
  statusLabel,
  todayISO,
} from "../lib/utils";
import type {
  AttendanceRecord,
  AttendanceStatus,
  PageId,
  SchoolClass,
  Student,
} from "../types";
import "../styles/dashboard.css";

interface DashboardPageProps {
  onNavigate: (page: PageId) => void;
}

type StatusCounts = Record<AttendanceStatus, number>;

interface ClassAttention {
  schoolClass: SchoolClass;
  expected: number;
  recorded: number;
  missing: number;
  late: number;
  absent: number;
  excused: number;
  completion: number;
}

const emptyStatusCounts = (): StatusCounts => ({
  present: 0,
  late: 0,
  absent: 0,
  excused: 0,
});

const uniqueLatestRecords = (records: AttendanceRecord[]) => {
  const byStudent = new Map<string, AttendanceRecord>();
  records.forEach((record) => {
    const current = byStudent.get(record.studentId);
    if (!current || new Date(record.timestamp).getTime() > new Date(current.timestamp).getTime()) {
      byStudent.set(record.studentId, record);
    }
  });
  return Array.from(byStudent.values());
};

const countStatuses = (records: AttendanceRecord[]) =>
  records.reduce<StatusCounts>((counts, record) => {
    counts[record.status] += 1;
    return counts;
  }, emptyStatusCounts());

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Selamat pagi";
  if (hour < 15) return "Selamat tengah hari";
  return "Selamat petang";
};

const methodLabel: Record<AttendanceRecord["method"], string> = {
  qr: "Imbasan QR",
  manual: "Rekod manual",
  admin: "Rekod admin",
};

function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="dashboard-section-heading">
      <div>
        {eyebrow && <span className="dashboard-section-heading__eyebrow">{eyebrow}</span>}
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </header>
  );
}

function ProgressTrack({
  value,
  max,
  label,
}: {
  value: number;
  max: number;
  label: string;
}) {
  const percentage = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      className="dashboard-progress"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={`${value} daripada ${max}, ${percentage}%`}
    >
      <span style={{ "--dashboard-progress": `${percentage}%` } as CSSProperties} />
    </div>
  );
}

function RecentRecords({
  records,
  studentById,
  limit = 6,
  showClass = true,
}: {
  records: AttendanceRecord[];
  studentById: Map<string, Student>;
  limit?: number;
  showClass?: boolean;
}) {
  const items = [...records]
    .sort(
      (first, second) =>
        new Date(second.timestamp).getTime() - new Date(first.timestamp).getTime(),
    )
    .flatMap((record) => {
      const student = studentById.get(record.studentId);
      return student ? [{ record, student }] : [];
    })
    .slice(0, limit);

  if (items.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="Belum ada rekod hari ini"
        message="Rekod yang sah akan muncul di sini selepas kehadiran murid dicatat."
      />
    );
  }

  return (
    <ol className="dashboard-record-list" aria-label="Rekod kehadiran terkini">
      {items.map(({ record, student }, index) => (
        <li key={record.id} className="dashboard-record">
          <Avatar
            initials={student.initials}
            size="sm"
            tone={index}
            label={`Murid ${student.name}`}
          />
          <div className="dashboard-record__identity">
            <strong>{student.name}</strong>
            <span>
              {showClass && `${student.className} · `}
              {methodLabel[record.method]}
            </span>
          </div>
          <div className="dashboard-record__state">
            <StatusBadge status={record.status} compact />
            <time dateTime={record.timestamp}>{formatTime(record.timestamp)}</time>
          </div>
        </li>
      ))}
    </ol>
  );
}

function TeacherDashboard({
  onNavigate,
  today,
  teacherName,
  assignedClass,
  classStudents,
  classRecords,
  studentById,
}: {
  onNavigate: DashboardPageProps["onNavigate"];
  today: string;
  teacherName: string;
  assignedClass?: SchoolClass;
  classStudents: Student[];
  classRecords: AttendanceRecord[];
  studentById: Map<string, Student>;
}) {
  const className = assignedClass?.name ?? "Kelas belum ditetapkan";
  const expected = classStudents.length;
  const recordedIds = new Set(classRecords.map((record) => record.studentId));
  const remainingStudents = classStudents.filter((student) => !recordedIds.has(student.id));
  const recorded = classRecords.length;
  const completion = expected > 0 ? Math.round((recorded / expected) * 100) : 0;
  const statusCounts = countStatuses(classRecords);
  const mostRecent = [...classRecords].sort(
    (first, second) =>
      new Date(second.timestamp).getTime() - new Date(first.timestamp).getTime(),
  )[0];

  const statusItems: Array<{
    status: AttendanceStatus | "unrecorded";
    label: string;
    value: number;
    icon: typeof CheckCircle2;
  }> = [
    {
      status: "present",
      label: statusLabel.present,
      value: statusCounts.present,
      icon: CheckCircle2,
    },
    {
      status: "late",
      label: statusLabel.late,
      value: statusCounts.late,
      icon: Clock3,
    },
    {
      status: "absent",
      label: statusLabel.absent,
      value: statusCounts.absent,
      icon: AlertTriangle,
    },
    {
      status: "excused",
      label: statusLabel.excused,
      value: statusCounts.excused,
      icon: BookOpenCheck,
    },
    {
      status: "unrecorded",
      label: "Belum direkod",
      value: remainingStudents.length,
      icon: ListChecks,
    },
  ];

  return (
    <main className="dashboard dashboard--teacher">
      <PageHeader
        eyebrow={`Guru kelas · ${className}`}
        title={`${getGreeting()}, ${teacherName}`}
        description={formatDayDate(today)}
        actions={
          <Button
            variant="secondary"
            icon={ClipboardCheck}
            onClick={() => onNavigate("attendance")}
          >
            Lihat kehadiran
          </Button>
        }
      />

      <section className="teacher-command" aria-labelledby="teacher-attendance-title">
        <div className="teacher-command__copy">
          <span className="teacher-command__session">
            <span aria-hidden="true" />
            Sesi hari ini
          </span>
          <h2 id="teacher-attendance-title">Kehadiran {className}</h2>
          <p>
            Rekod <strong>{recorded}</strong> daripada <strong>{expected}</strong> murid aktif.
            {remainingStudents.length > 0
              ? ` Tinggal ${remainingStudents.length} murid lagi.`
              : " Semua murid telah disemak."}
          </p>
          <Button
            icon={ScanLine}
            iconAfter={ArrowRight}
            className="teacher-command__scan"
            onClick={() => onNavigate("scan")}
          >
            Mula imbas
          </Button>
          <span className="teacher-command__hint">
            Imbas kod QR murid untuk merekod kehadiran sekarang.
          </span>
        </div>

        <div className="teacher-register" aria-label={`Kemajuan kelas ${completion}%`}>
          <div
            className="teacher-register__dial"
            style={{ "--dashboard-progress": `${completion}%` } as CSSProperties}
          >
            <span>
              <strong>{completion}%</strong>
              direkod
            </span>
          </div>
          <ProgressTrack
            value={recorded}
            max={expected}
            label={`Kemajuan rekod kehadiran ${className}`}
          />
          <p>
            {mostRecent
              ? `Rekod terakhir pada ${formatTime(mostRecent.timestamp)}`
              : "Belum ada rekod untuk hari ini"}
          </p>
        </div>
      </section>

      <section className="teacher-status-band" aria-labelledby="teacher-status-title">
        <div className="teacher-status-band__intro">
          <span id="teacher-status-title">Status kelas</span>
          <button type="button" onClick={() => onNavigate("attendance")}>
            Buka butiran <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="teacher-status-band__items">
          {statusItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                className={`teacher-status teacher-status--${item.status}`}
                key={item.status}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            );
          })}
        </div>
      </section>

      <div className="teacher-work-grid">
        <section className="dashboard-panel" aria-labelledby="remaining-title">
          <SectionHeading
            eyebrow="Tindakan seterusnya"
            title="Belum direkod"
            description={
              remainingStudents.length > 0
                ? `${remainingStudents.length} murid masih menunggu rekod hari ini.`
                : "Semua murid aktif sudah mempunyai rekod."
            }
            action={
              remainingStudents.length > 0 ? (
                <span className="dashboard-count-badge">{remainingStudents.length}</span>
              ) : undefined
            }
          />
          {remainingStudents.length > 0 ? (
            <>
              <ul className="remaining-list" aria-labelledby="remaining-title">
                {remainingStudents.slice(0, 6).map((student, index) => (
                  <li key={student.id}>
                    <Avatar
                      initials={student.initials}
                      size="sm"
                      tone={index + 2}
                      label={`Murid ${student.name}`}
                    />
                    <span>
                      <strong>{student.name}</strong>
                      <small>{student.code}</small>
                    </span>
                    <span className="remaining-list__state">Belum direkod</span>
                  </li>
                ))}
              </ul>
              <div className="dashboard-panel__footer">
                <Button
                  variant="secondary"
                  icon={ClipboardCheck}
                  onClick={() => onNavigate("attendance")}
                >
                  Semak senarai penuh
                </Button>
                <Button icon={QrCode} onClick={() => onNavigate("scan")}>
                  Imbas sekarang
                </Button>
              </div>
            </>
          ) : (
            <div className="teacher-complete-state">
              <CheckCircle2 size={24} aria-hidden="true" />
              <div>
                <strong>Rekod kelas lengkap</strong>
                <p>Tiada murid aktif yang tertinggal untuk hari ini.</p>
              </div>
            </div>
          )}
        </section>

        <section className="dashboard-panel" aria-labelledby="teacher-recent-title">
          <SectionHeading
            eyebrow="Hari ini"
            title="Rekod terkini"
            description="Rekod sah bagi kelas anda, disusun mengikut masa."
            action={
              <Button
                variant="tertiary"
                size="compact"
                onClick={() => onNavigate("attendance")}
              >
                Lihat semua
              </Button>
            }
          />
          <RecentRecords
            records={classRecords}
            studentById={studentById}
            limit={6}
            showClass={false}
          />
        </section>
      </div>
    </main>
  );
}

function AdminStatusBand({
  activeStudents,
  recorded,
  statusCounts,
  missing,
  onNavigate,
}: {
  activeStudents: number;
  recorded: number;
  statusCounts: StatusCounts;
  missing: number;
  onNavigate: DashboardPageProps["onNavigate"];
}) {
  return (
    <section className="admin-status-band" aria-labelledby="admin-status-title">
      <header className="admin-status-band__header">
        <div>
          <span className="dashboard-section-heading__eyebrow">Skop seluruh sekolah</span>
          <h2 id="admin-status-title">Keadaan kehadiran hari ini</h2>
        </div>
        <Button
          variant="tertiary"
          size="compact"
          iconAfter={ChevronRight}
          onClick={() => onNavigate("attendance")}
        >
          Lihat semua rekod
        </Button>
      </header>
      <div className="admin-status-band__metrics">
        <div className="admin-metric">
          <Users size={20} aria-hidden="true" />
          <span>Murid aktif</span>
          <strong>{activeStudents}</strong>
          <small>Jangkaan hari ini</small>
        </div>
        <div className="admin-metric admin-metric--primary">
          <ClipboardCheck size={20} aria-hidden="true" />
          <span>Sudah direkod</span>
          <strong>{recorded}</strong>
          <small>Semua status</small>
        </div>
        <div className="admin-metric admin-metric--success">
          <CheckCircle2 size={20} aria-hidden="true" />
          <span>Hadir</span>
          <strong>{statusCounts.present}</strong>
          <small>{statusCounts.late} lewat</small>
        </div>
        <div className={`admin-metric ${missing > 0 ? "admin-metric--warning" : ""}`}>
          {missing > 0 ? (
            <AlertTriangle size={20} aria-hidden="true" />
          ) : (
            <CheckCircle2 size={20} aria-hidden="true" />
          )}
          <span>Belum direkod</span>
          <strong>{missing}</strong>
          <small>
            {statusCounts.absent} tidak hadir · {statusCounts.excused} berizin
          </small>
        </div>
      </div>
      <ProgressTrack
        value={recorded}
        max={activeStudents}
        label="Kemajuan rekod kehadiran seluruh sekolah"
      />
    </section>
  );
}

function AttentionQueue({
  items,
  onNavigate,
}: {
  items: ClassAttention[];
  onNavigate: DashboardPageProps["onNavigate"];
}) {
  if (items.length === 0) {
    return (
      <div className="admin-queue-empty">
        <CheckCircle2 size={24} aria-hidden="true" />
        <div>
          <strong>Semua kelas sudah dikemas kini</strong>
          <p>Tiada rekod tertinggal atau pengecualian untuk disemak.</p>
        </div>
      </div>
    );
  }

  return (
    <ol className="attention-queue">
      {items.slice(0, 7).map((item) => {
        const issueLabels = [
          item.missing > 0 ? `${item.missing} belum direkod` : null,
          item.absent > 0 ? `${item.absent} tidak hadir` : null,
          item.late > 0 ? `${item.late} lewat` : null,
          item.excused > 0 ? `${item.excused} berizin` : null,
        ].filter((label): label is string => Boolean(label));

        return (
          <li key={item.schoolClass.id}>
            <div className="attention-queue__identity">
              <span className="attention-queue__rank" aria-hidden="true">
                {String(items.indexOf(item) + 1).padStart(2, "0")}
              </span>
              <div>
                <strong>{item.schoolClass.name}</strong>
                <span>{item.schoolClass.teacher}</span>
              </div>
            </div>
            <div className="attention-queue__progress">
              <span>
                <strong>
                  {item.recorded}/{item.expected}
                </strong>
                <small>{item.completion}% direkod</small>
              </span>
              <ProgressTrack
                value={item.recorded}
                max={item.expected}
                label={`Kemajuan ${item.schoolClass.name}`}
              />
            </div>
            <div className="attention-queue__issues">
              {issueLabels.slice(0, 2).map((label, index) => (
                <span
                  className={
                    index === 0 && item.missing > 0
                      ? "attention-queue__issue attention-queue__issue--warning"
                      : "attention-queue__issue"
                  }
                  key={label}
                >
                  {label}
                </span>
              ))}
              {issueLabels.length > 2 && (
                <span className="attention-queue__more">+{issueLabels.length - 2}</span>
              )}
            </div>
            <Button
              variant="tertiary"
              size="compact"
              iconAfter={ChevronRight}
              aria-label={`Semak kehadiran ${item.schoolClass.name}`}
              onClick={() => onNavigate("attendance")}
            >
              Semak
            </Button>
          </li>
        );
      })}
    </ol>
  );
}

function WeeklyChart({
  attendance,
  activeStudentById,
  activeStudentCount,
  today,
}: {
  attendance: AttendanceRecord[];
  activeStudentById: Map<string, Student>;
  activeStudentCount: number;
  today: string;
}) {
  const validHistory = attendance.filter(
    (record) => record.date <= today && activeStudentById.has(record.studentId),
  );
  const dates = Array.from(new Set(validHistory.map((record) => record.date)))
    .sort()
    .slice(-6);
  const days = dates.map((date) => {
    const records = uniqueLatestRecords(validHistory.filter((record) => record.date === date));
    const attending = records.filter(
      (record) => record.status === "present" || record.status === "late",
    ).length;
    const rate =
      activeStudentCount > 0 ? Math.round((attending / activeStudentCount) * 100) : 0;
    return { date, attending, recorded: records.length, rate };
  });

  if (days.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="Belum cukup data"
        message="Carta akan dibina daripada rekod kehadiran sebenar apabila data tersedia."
      />
    );
  }

  return (
    <>
      <div
        className="weekly-chart"
        role="img"
        aria-label={`Carta kadar murid hadir dan lewat bagi ${days.length} hari rekod terkini`}
      >
        <div className="weekly-chart__scale" aria-hidden="true">
          <span>100%</span>
          <span>50%</span>
          <span>0%</span>
        </div>
        <ol>
          {days.map((day) => (
            <li
              key={day.date}
              title={`${formatDate(day.date)}: ${day.attending} hadir atau lewat daripada ${activeStudentCount} murid aktif (${day.rate}%)`}
            >
              <span className="weekly-chart__value">{day.rate}%</span>
              <span className="weekly-chart__column" aria-hidden="true">
                <span
                  style={
                    {
                      "--weekly-value": `${Math.max(day.rate, 2)}%`,
                    } as CSSProperties
                  }
                />
              </span>
              <time dateTime={day.date}>
                {formatDate(day.date, {
                  weekday: "short",
                  day: "numeric",
                  month: undefined,
                  year: undefined,
                })}
              </time>
              <small>{day.recorded} rekod</small>
            </li>
          ))}
        </ol>
      </div>
      <ul className="sr-only">
        {days.map((day) => (
          <li key={day.date}>
            {formatDate(day.date)}: {day.attending} murid hadir atau lewat daripada{" "}
            {activeStudentCount} murid aktif, bersamaan {day.rate} peratus. {day.recorded} rekod
            disimpan.
          </li>
        ))}
      </ul>
    </>
  );
}

function AdminDashboard({
  onNavigate,
  today,
  attendance,
  activeStudents,
  todayRecords,
  studentById,
  activeStudentById,
  classAttention,
}: {
  onNavigate: DashboardPageProps["onNavigate"];
  today: string;
  attendance: AttendanceRecord[];
  activeStudents: Student[];
  todayRecords: AttendanceRecord[];
  studentById: Map<string, Student>;
  activeStudentById: Map<string, Student>;
  classAttention: ClassAttention[];
}) {
  const statusCounts = countStatuses(todayRecords);
  const missing = Math.max(0, activeStudents.length - todayRecords.length);
  const attentionItems = classAttention.filter(
    (item) => item.missing > 0 || item.late > 0 || item.absent > 0 || item.excused > 0,
  );

  return (
    <main className="dashboard dashboard--admin">
      <PageHeader
        eyebrow="Operasi pagi · Semua kelas"
        title="Ringkasan hari ini"
        description={`${formatDayDate(today)} · ${activeStudents.length} murid aktif`}
        actions={
          <>
            <Button variant="secondary" icon={QrCode} onClick={() => onNavigate("scan")}>
              Imbas QR
            </Button>
            <Button
              icon={ClipboardCheck}
              onClick={() => onNavigate("attendance")}
            >
              Semak kehadiran
            </Button>
          </>
        }
      />

      <AdminStatusBand
        activeStudents={activeStudents.length}
        recorded={todayRecords.length}
        statusCounts={statusCounts}
        missing={missing}
        onNavigate={onNavigate}
      />

      <div className="admin-decision-grid">
        <section
          className="dashboard-panel dashboard-panel--queue"
          aria-labelledby="attention-title"
        >
          <SectionHeading
            eyebrow="Susunan ikut keutamaan"
            title="Kelas yang perlu perhatian"
            description="Kelas belum lengkap didahulukan, kemudian rekod pengecualian."
            action={
              <span className="dashboard-count-badge">{attentionItems.length}</span>
            }
          />
          <AttentionQueue items={attentionItems} onNavigate={onNavigate} />
          {attentionItems.length > 7 && (
            <div className="dashboard-panel__footer dashboard-panel__footer--end">
              <Button
                variant="secondary"
                size="compact"
                iconAfter={ArrowRight}
                onClick={() => onNavigate("attendance")}
              >
                Lihat {attentionItems.length} kelas
              </Button>
            </div>
          )}
        </section>

        <section className="dashboard-panel dashboard-panel--chart" aria-labelledby="weekly-title">
          <SectionHeading
            eyebrow="Berdasarkan rekod tersimpan"
            title="Kehadiran minggu ini"
            description="Kadar hadir termasuk murid lewat bagi enam hari rekod terkini."
          />
          <WeeklyChart
            attendance={attendance}
            activeStudentById={activeStudentById}
            activeStudentCount={activeStudents.length}
            today={today}
          />
          <div className="weekly-chart__note">
            <BarChart3 size={17} aria-hidden="true" />
            <span>Nilai dikira terus daripada rekod kehadiran, tanpa anggaran trend.</span>
          </div>
        </section>
      </div>

      <div className="admin-activity-grid">
        <section className="dashboard-panel" aria-labelledby="recent-title">
          <SectionHeading
            eyebrow="Aktiviti langsung"
            title="Rekod terkini"
            description="Rekod sah hari ini daripada semua kelas."
            action={
              <Button
                variant="tertiary"
                size="compact"
                onClick={() => onNavigate("attendance")}
              >
                Lihat semua
              </Button>
            }
          />
          <RecentRecords records={todayRecords} studentById={studentById} limit={6} />
        </section>

        <section className="dashboard-panel dashboard-panel--actions" aria-labelledby="actions-title">
          <SectionHeading
            eyebrow="Jalan pintas"
            title="Tindakan operasi"
            description="Terus ke ruang kerja yang diperlukan."
          />
          <div className="admin-actions" id="actions-title">
            <button type="button" onClick={() => onNavigate("students")}>
              <span className="admin-actions__icon">
                <UserPlus size={20} aria-hidden="true" />
              </span>
              <span>
                <strong>Urus murid</strong>
                <small>Semak atau tambah profil</small>
              </span>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
            <button type="button" onClick={() => onNavigate("classes")}>
              <span className="admin-actions__icon">
                <School size={20} aria-hidden="true" />
              </span>
              <span>
                <strong>Urus kelas</strong>
                <small>Guru, bilik dan senarai kelas</small>
              </span>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
            <button type="button" onClick={() => onNavigate("reports")}>
              <span className="admin-actions__icon">
                <CalendarDays size={20} aria-hidden="true" />
              </span>
              <span>
                <strong>Buka laporan</strong>
                <small>Semak corak dan eksport data</small>
              </span>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}

export function DashboardPage({ onNavigate }: DashboardPageProps) {
  const { session, students, attendance, classes } = useApp();
  const today = todayISO();
  const activeStudents = students.filter((student) => student.active);
  const studentById = new Map(students.map((student) => [student.id, student]));
  const activeStudentById = new Map(activeStudents.map((student) => [student.id, student]));
  const todayRecords = uniqueLatestRecords(
    attendance.filter(
      (record) => record.date === today && activeStudentById.has(record.studentId),
    ),
  );
  const todayRecordByStudent = new Map(
    todayRecords.map((record) => [record.studentId, record]),
  );
  const assignedClass = classes.find(
    (schoolClass) => schoolClass.id === session?.assignedClassId,
  );
  const classStudents = activeStudents.filter(
    (student) => student.classId === assignedClass?.id,
  );
  const classRecords = todayRecords.filter(
    (record) => studentById.get(record.studentId)?.classId === assignedClass?.id,
  );

  const classAttention = classes
    .map<ClassAttention>((schoolClass) => {
      const classMembers = activeStudents.filter(
        (student) => student.classId === schoolClass.id,
      );
      const records = classMembers.flatMap((student) => {
        const record = todayRecordByStudent.get(student.id);
        return record ? [record] : [];
      });
      const counts = countStatuses(records);
      const expected = classMembers.length;
      const recorded = records.length;
      return {
        schoolClass,
        expected,
        recorded,
        missing: Math.max(0, expected - recorded),
        late: counts.late,
        absent: counts.absent,
        excused: counts.excused,
        completion: expected > 0 ? Math.round((recorded / expected) * 100) : 0,
      };
    })
    .sort(
      (first, second) =>
        second.missing - first.missing ||
        second.absent - first.absent ||
        second.late + second.excused - (first.late + first.excused) ||
        first.schoolClass.name.localeCompare(second.schoolClass.name, "ms"),
    );

  if (session?.role === "teacher") {
    return (
      <TeacherDashboard
        onNavigate={onNavigate}
        today={today}
        teacherName={session.name}
        assignedClass={assignedClass}
        classStudents={classStudents}
        classRecords={classRecords}
        studentById={studentById}
      />
    );
  }

  return (
    <AdminDashboard
      onNavigate={onNavigate}
      today={today}
      attendance={attendance}
      activeStudents={activeStudents}
      todayRecords={todayRecords}
      studentById={studentById}
      activeStudentById={activeStudentById}
      classAttention={classAttention}
    />
  );
}

